/**
 * OperationInvoicesModal — Invoices (billing) de uma operação de crédito.
 * Cross-domain: a tela é do settlement, mas os invoices vêm da REST do billing.
 */
import { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import BadgeStatus from '../../../atoms/BadgeStatus';
import { listInvoicesByOperation } from '../../billing/services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink';

const statusColors: Record<string, BadgeColor> = {
    OPEN: 'yellow',
    PAID: 'green',
    PARTIALLY_PAID: 'orange',
    CANCELLED: 'red',
};

interface Props {
    operation: any;
    onClose: () => void;
}

const OperationInvoicesModal: React.FC<Props> = ({ operation, onClose }) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const data = await listInvoicesByOperation(operation.credit_operation_id);
                setRows(data.list || []);
            } catch { setRows([]); }
            setLoading(false);
        })();
    }, [operation.credit_operation_id]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Faturas — {operation.contract_number}
                        {operation.amendment_number > 0 ? `-${operation.amendment_number}` : ''}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                {loading ? (
                    <p className="text-center text-gray-500 py-4">Carregando...</p>
                ) : rows.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Nenhuma fatura encontrada</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-slate-700">
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                <th className="px-3 py-2">Fatura</th>
                                <th className="px-3 py-2">Recebível</th>
                                <th className="px-3 py-2 text-right">Valor</th>
                                <th className="px-3 py-2 text-right">Valor pago</th>
                                <th className="px-3 py-2">Vencimento</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2 text-center">Cobrança</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((inv: any) => (
                                <tr key={inv.id} className="border-b border-gray-100 dark:border-slate-700">
                                    <td className="px-3 py-2 text-gray-500 font-mono text-xs" title={inv.invoiceId}>
                                        {inv.invoiceId ? inv.invoiceId.slice(0, 8) : '-'}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs text-gray-500" title={inv.chargeId}>
                                        {inv.chargeId ? inv.chargeId.slice(0, 8) : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-right">{formatBRL(inv.amount)}</td>
                                    <td className="px-3 py-2 text-right">{formatBRL(inv.amountPaid)}</td>
                                    <td className="px-3 py-2">{formatDateBR(inv.dueDate)}</td>
                                    <td className="px-3 py-2">
                                        <BadgeStatus text={inv.statusFormatted || inv.status} color={statusColors[inv.status] || 'gray'} />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        {inv.collect
                                            ? <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded font-medium">SIM</span>
                                            : <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded font-medium">NÃO</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default OperationInvoicesModal;
